import { Text } from '@/components/ui/text';
import { EVENT_POSTER_CONSTANTS } from '@/lib/api/apiTypes';
import { DESIGN_TOKENS } from '@/lib/design/theme';
import { Image } from 'expo-image';
import * as React from 'react';
import { Image as RNImage, Modal, Platform, Pressable, View } from 'react-native';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withSpring,
} from 'react-native-reanimated';
import Svg, { Path } from 'react-native-svg';
import type { DraftPoster } from './types';
import { clampNumber, clampPosterImagePan, clampUnit } from './utils';

const AnimatedImage = Animated.createAnimatedComponent(Image);

// Must match UnifiedFeedCard's rounded-[28px] so the crop preview is pixel-accurate.
const FRAME_RADIUS = 28;
const FRAME_H_MARGIN = 16; // 16px each side = 32px total

const MIN_SCALE = 1; // scale=1 means image just covers the frame — cannot zoom out beyond this
const MAX_SCALE = 4;

const SPRING = { damping: 18, stiffness: 220 } as const;

type Rect = { x: number; y: number; width: number; height: number };
type FrameRect = { left: number; top: number; width: number; height: number };

/**
 * Given image dimensions and a frame, compute the image rect that makes the
 * image cover the frame at scale=1, centered on the frame center.
 */
function buildCoverImageRect(
  frame: FrameRect,
  imageNaturalWidth: number,
  imageNaturalHeight: number,
): Rect {
  const coverScale = Math.max(
    frame.width / imageNaturalWidth,
    frame.height / imageNaturalHeight,
  );
  const w = imageNaturalWidth * coverScale;
  const h = imageNaturalHeight * coverScale;
  const cx = frame.left + frame.width / 2;
  const cy = frame.top + frame.height / 2;
  return { x: cx - w / 2, y: cy - h / 2, width: w, height: h };
}

/**
 * Compute initial pan so that the given focal point is centered inside the frame.
 * Clamps the result so the image never reveals empty space.
 */
function computeInitialPan(
  imageBase: Rect,
  frame: FrameRect,
  fpX: number,
  fpY: number,
  scale: number = 1,
): { x: number; y: number } {
  const scaledW = imageBase.width * scale;
  const scaledH = imageBase.height * scale;
  const imageLeft = imageBase.x - (scaledW - imageBase.width) / 2;
  const imageTop = imageBase.y - (scaledH - imageBase.height) / 2;
  const overflowX = Math.max(0, scaledW - frame.width);
  const overflowY = Math.max(0, scaledH - frame.height);
  const targetPanX = -(overflowX * fpX) - (imageLeft - frame.left);
  const targetPanY = -(overflowY * fpY) - (imageTop - frame.top);
  return clampPosterImagePan({
    imageRect: imageBase,
    scale,
    panX: targetPanX,
    panY: targetPanY,
    frameRect: frame,
  });
}

export function PosterFocalPointModal({
  poster,
  visible,
  focalPointX,
  focalPointY,
  focalScale,
  onClose,
  onDone,
}: {
  poster: DraftPoster | null;
  visible: boolean;
  focalPointX: number;
  focalPointY: number;
  focalScale: number;
  onClose: () => void;
  onDone: (x: number, y: number, scale: number) => void;
}) {
  const [containerSize, setContainerSize] = React.useState({ width: 0, height: 0 });
  const [resolvedSize, setResolvedSize] = React.useState<{ width: number; height: number } | null>(null);
  // Resolve natural image dimensions for server-loaded posters that have no stored width/height.
  React.useEffect(() => {
    if (!visible) return;
    if (poster?.width && poster?.height) return;
    if (!poster?.uri) return;
    RNImage.getSize(
      poster.uri,
      (width, height) => setResolvedSize({ width, height }),
      () => {}
    );
  }, [visible, poster?.uri, poster?.width, poster?.height]);

  // Shared values so gesture worklets always read the latest geometry.
  const frameShared = useSharedValue<FrameRect>({ left: 0, top: 0, width: 0, height: 0 });
  const imageBaseShared = useSharedValue<Rect>({ x: 0, y: 0, width: 0, height: 0 });

  const scale = useSharedValue(MIN_SCALE);
  const savedScale = useSharedValue(MIN_SCALE);
  const panX = useSharedValue(0);
  const panY = useSharedValue(0);
  const savedPanX = useSharedValue(0);
  const savedPanY = useSharedValue(0);
  const hasInitialized = React.useRef(false);

  // Derived JS-side values for Done/Reset handlers.
  const frameRect = React.useMemo<FrameRect | null>(() => {
    const { width, height } = containerSize;
    if (!width || !height) return null;
    // Constrain frame to fit within both dimensions so it never overflows the canvas.
    const maxFw = width - FRAME_H_MARGIN * 2;
    const maxFh = height - FRAME_H_MARGIN * 2;
    const fw = Math.min(maxFw, maxFh * (4 / 5));
    const fh = fw * (5 / 4);
    return { left: (width - fw) / 2, top: (height - fh) / 2, width: fw, height: fh };
  }, [containerSize]);

  const imageBase = React.useMemo<Rect | null>(() => {
    const effectiveWidth = poster?.width ?? resolvedSize?.width ?? null;
    const effectiveHeight = poster?.height ?? resolvedSize?.height ?? null;
    if (!frameRect || !effectiveWidth || !effectiveHeight) return null;
    return buildCoverImageRect(frameRect, effectiveWidth, effectiveHeight);
  }, [frameRect, poster?.width, poster?.height, resolvedSize]);

  const imageBaseStyle = React.useMemo(
    () =>
      imageBase
        ? {
            position: 'absolute' as const,
            left: imageBase.x,
            top: imageBase.y,
            width: imageBase.width,
            height: imageBase.height,
          }
        : null,
    [imageBase],
  );

  const imageInFrameBaseStyle = React.useMemo(
    () =>
      imageBase && frameRect
        ? {
            position: 'absolute' as const,
            left: imageBase.x - frameRect.left,
            top: imageBase.y - frameRect.top,
            width: imageBase.width,
            height: imageBase.height,
          }
        : null,
    [frameRect, imageBase],
  );

  React.useEffect(() => {
    if (!frameRect) return;
    frameShared.value = frameRect;
  }, [frameRect, frameShared]);

  React.useEffect(() => {
    if (!imageBase) return;
    imageBaseShared.value = imageBase;
  }, [imageBase, imageBaseShared]);

  React.useEffect(() => {
    if (!visible) {
      hasInitialized.current = false;
      return;
    }
    if (!frameRect || !imageBase) return;
    if (hasInitialized.current) return;
    hasInitialized.current = true;
    const restoredScale = Math.max(MIN_SCALE, focalScale ?? MIN_SCALE);
    const init = computeInitialPan(imageBase, frameRect, focalPointX, focalPointY, restoredScale);
    scale.value = restoredScale;
    savedScale.value = restoredScale;
    panX.value = init.x;
    panY.value = init.y;
    savedPanX.value = init.x;
    savedPanY.value = init.y;
  }, [
    visible, frameRect, imageBase,
    focalPointX, focalPointY, focalScale,
    frameShared, imageBaseShared,
    scale, savedScale, panX, panY, savedPanX, savedPanY,
  ]);

  const gesture = React.useMemo(
    () =>
      Gesture.Simultaneous(
        Gesture.Pinch()
          .runOnJS(false)
          .onStart(() => {
            savedScale.value = scale.value;
          })
          .onUpdate((event) => {
            const next = clampNumber(savedScale.value * event.scale, MIN_SCALE, MAX_SCALE);
            scale.value = next;
            const clamped = clampPosterImagePan({
              imageRect: imageBaseShared.value,
              scale: next,
              panX: panX.value,
              panY: panY.value,
              frameRect: frameShared.value,
            });
            panX.value = clamped.x;
            panY.value = clamped.y;
          })
          .onEnd(() => {
            savedScale.value = scale.value;
            savedPanX.value = panX.value;
            savedPanY.value = panY.value;
          }),
        Gesture.Pan()
          .runOnJS(false)
          .onStart(() => {
            savedPanX.value = panX.value;
            savedPanY.value = panY.value;
          })
          .onUpdate((event) => {
            const clamped = clampPosterImagePan({
              imageRect: imageBaseShared.value,
              scale: scale.value,
              panX: savedPanX.value + event.translationX,
              panY: savedPanY.value + event.translationY,
              frameRect: frameShared.value,
            });
            panX.value = clamped.x;
            panY.value = clamped.y;
          })
          .onEnd(() => {
            savedPanX.value = panX.value;
            savedPanY.value = panY.value;
          }),
      ),
    [frameShared, imageBaseShared, panX, panY, savedPanX, savedPanY, savedScale, scale],
  );

  const imageStyle = useAnimatedStyle(() => ({
    transform: [{ translateX: panX.value }, { translateY: panY.value }, { scale: scale.value }],
  }));

  const imageInFrameStyle = useAnimatedStyle(() => ({
    transform: [{ translateX: panX.value }, { translateY: panY.value }, { scale: scale.value }],
  }));

  function handleDone() {
    if (!frameRect || !imageBase) {
      onDone(clampUnit(focalPointX), clampUnit(focalPointY), MIN_SCALE);
      return;
    }
    const imageLeft = imageBase.x + panX.value + (imageBase.width * (1 - scale.value)) / 2;
    const imageTop = imageBase.y + panY.value + (imageBase.height * (1 - scale.value)) / 2;
    const scaledW = imageBase.width * scale.value;
    const scaledH = imageBase.height * scale.value;
    const relativeLeft = imageLeft - frameRect.left;
    const relativeTop = imageTop - frameRect.top;
    const overflowX = scaledW - frameRect.width;
    const overflowY = scaledH - frameRect.height;
    const focalX = overflowX > 0 ? clampUnit(-relativeLeft / overflowX) : 0.5;
    const focalY = overflowY > 0 ? clampUnit(-relativeTop / overflowY) : 0.3;
    onDone(focalX, focalY, scale.value);
  }

  function handleReset() {
    if (!frameRect || !imageBase) return;
    const def = EVENT_POSTER_CONSTANTS.FOCAL_POINT_DEFAULT;
    const init = computeInitialPan(imageBase, frameRect, def.x, def.y);
    scale.value = withSpring(MIN_SCALE, SPRING);
    panX.value = withSpring(init.x, SPRING);
    panY.value = withSpring(init.y, SPRING);
    savedScale.value = MIN_SCALE;
    savedPanX.value = init.x;
    savedPanY.value = init.y;
  }

  const imageUri = poster?.publicURL ?? poster?.uri;
  const headerPadTop = Platform.OS === 'ios' ? 56 : 32;

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View
        style={{ flex: 1, backgroundColor: '#0a0a0e' }}
        onLayout={(e) =>
          setContainerSize({
            width: e.nativeEvent.layout.width,
            height: e.nativeEvent.layout.height,
          })
        }>
        {imageUri ? (
          <AnimatedImage
            source={{ uri: imageUri }}
            contentFit="fill"
            cachePolicy="memory-disk"
            transition={200}
            style={[
              imageBaseStyle,
              imageStyle,
              {
                zIndex: 1,
              },
            ]}
          />
        ) : null}

        {frameRect ? (
          <View
            pointerEvents="none"
            style={{ position: 'absolute', top: 0, left: 0, zIndex: 10 }}>
            {(() => {
              const r = FRAME_RADIUS;
              const x = frameRect.left;
              const y = frameRect.top;
              const w = frameRect.width;
              const h = frameRect.height;
              const k = 0.5523 * r;
              const framePath = [
                `M ${x + r} ${y}`,
                `H ${x + w - r}`,
                `C ${x + w - r + k} ${y} ${x + w} ${y + r - k} ${x + w} ${y + r}`,
                `V ${y + h - r}`,
                `C ${x + w} ${y + h - r + k} ${x + w - r + k} ${y + h} ${x + w - r} ${y + h}`,
                `H ${x + r}`,
                `C ${x + r - k} ${y + h} ${x} ${y + h - r + k} ${x} ${y + h - r}`,
                `V ${y + r}`,
                `C ${x} ${y + r - k} ${x + r - k} ${y} ${x + r} ${y}`,
                `Z`,
              ].join(' ');
              const outerPath = `M 0 0 H ${containerSize.width} V ${containerSize.height} H 0 Z`;

              return (
            <Svg width={containerSize.width} height={containerSize.height}>
              <Path
                fillRule="evenodd"
                fill="rgba(0,0,0,0.55)"
                d={`${outerPath} ${framePath}`}
              />
            </Svg>
              );
            })()}
          </View>
        ) : null}

        {frameRect && imageUri ? (
          <View
            pointerEvents="none"
            style={{
              position: 'absolute',
              top: frameRect.top,
              left: frameRect.left,
              width: frameRect.width,
              height: frameRect.height,
              borderRadius: FRAME_RADIUS,
              overflow: 'hidden',
              zIndex: 12,
            }}>
            <AnimatedImage
              source={{ uri: imageUri }}
              contentFit="fill"
              cachePolicy="memory-disk"
              transition={200}
              style={[imageInFrameBaseStyle, imageInFrameStyle]}
            />
          </View>
        ) : null}

        {frameRect ? (
          <View
            pointerEvents="none"
            style={{
              position: 'absolute',
              top: frameRect.top,
              left: frameRect.left,
              width: frameRect.width,
              height: frameRect.height,
              borderRadius: FRAME_RADIUS,
              borderWidth: 2,
              borderColor: 'rgba(255,255,255,0.5)',
              zIndex: 15,
            }}
          />
        ) : null}

        {/* Header */}
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingHorizontal: 20,
            paddingTop: headerPadTop,
            paddingBottom: 16,
            zIndex: 20,
          }}>
          <Text
            variant="large"
            style={{ fontWeight: '600', color: DESIGN_TOKENS.colors.text }}>
            Adjust poster crop
          </Text>
          <Pressable onPress={onClose} hitSlop={16}>
            <Text variant="small" style={{ color: DESIGN_TOKENS.colors.textSoft }}>
              Cancel
            </Text>
          </Pressable>
        </View>

        <GestureDetector gesture={gesture}>
          <View
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 8,
              backgroundColor: 'transparent',
            }}
          />
        </GestureDetector>

        {/* Bottom buttons */}
        <View
          style={{
            flexDirection: 'row',
            gap: 12,
            paddingHorizontal: 20,
            paddingTop: 16,
            paddingBottom: Platform.OS === 'ios' ? 48 : 24,
            zIndex: 20,
            marginTop: 'auto',
          }}>
          <Pressable
            onPress={handleReset}
            style={{
              flex: 1,
              borderRadius: 18,
              borderWidth: 1,
              borderColor: DESIGN_TOKENS.colors.borderStrong,
              backgroundColor: DESIGN_TOKENS.colors.surfaceAlt,
              alignItems: 'center',
              justifyContent: 'center',
              paddingVertical: 14,
            }}>
            <Text variant="small" style={{ fontWeight: '600', color: DESIGN_TOKENS.colors.text }}>
              Reset
            </Text>
          </Pressable>
          <Pressable
            onPress={handleDone}
            style={{
              flex: 1,
              borderRadius: 18,
              backgroundColor: DESIGN_TOKENS.colors.accent,
              alignItems: 'center',
              justifyContent: 'center',
              paddingVertical: 14,
            }}>
            <Text variant="small" style={{ color: '#111318', fontWeight: '700' }}>
              Done
            </Text>
          </Pressable>
        </View>
      </View>
    </Modal>
  );
}
